import { Injectable } from '@angular/core';
import { NavigationExtras, Router } from '@angular/router';
import { Order } from '@vendasta/sales-orders';
import { combineLatest, Observable } from 'rxjs';
import { map, shareReplay, take } from 'rxjs/operators';
import { ActivationParamService } from './activation-param.service';
import { ActivationService } from './activation.service';
import { OrderFormType } from './interface';

export interface NavigationLink {
  url: string;
  navOptions?: NavigationExtras;
  windowNavigation?: boolean;
}

export interface NavigationLinkMap {
  selection: NavigationLink;
  orderForm: NavigationLink;
  dynamicOrderForm: NavigationLink;
  confirmation: NavigationLink;
  manageAccounts: NavigationLink;
  accountDetails: NavigationLink;
  orderSubmitted: NavigationLink;
}

type ActivationStep = 'selection' | 'dynamic-order-form' | 'order-form' | 'confirmation';

@Injectable({
  providedIn: 'root',
})
export class ActivationNavigationService {
  private readonly navigationMap$: Observable<NavigationLinkMap>;

  constructor(
    private activationService: ActivationService,
    private router: Router,
    private paramService: ActivationParamService,
  ) {
    this.navigationMap$ = this.activationService.selectedQueryParams$.pipe(
      map((queryParams) => {
        const agid = queryParams.accountGroupId;
        return {
          selection: {
            url: `businesses/accounts/${agid}/activation`,
            navOptions: { queryParams },
          },
          orderForm: {
            url: `businesses/accounts/${agid}/activation/order-form`,
            navOptions: { queryParams },
          },
          dynamicOrderForm: {
            url: `businesses/accounts/${agid}/activation/dynamic-order-form`,
            navOptions: { queryParams },
          },
          confirmation: {
            url: `businesses/accounts/${agid}/activation/confirmation`,
            navOptions: { queryParams },
          },
          manageAccounts: { url: `/manage-accounts` },
          accountDetails: { url: `/businesses/accounts/${agid}/details` },
          orderSubmitted: {
            url: `/businesses/accounts/${agid}/order-submitted-page`,
          },
        };
      }),
      shareReplay(1),
    );
  }

  navigateToNextStep(fromStep: ActivationStep, order?: Order): void {
    combineLatest([
      this.navigationMap$,
      this.activationService.selectedItemsRequireOrderForm$,
      this.paramService.navigationSource$,
    ])
      .pipe(take(1))
      .subscribe(([navigationMap, itemsRequireOrderForm, navigationSource]) => {
        const navigationSelection = getNextNavigationStep(
          fromStep,
          itemsRequireOrderForm,
          navigationMap,
          navigationSource,
          order,
        );
        this.clearSelectionsIfFromStepIsConfirm(fromStep);
        this.navigateToSelection(navigationSelection);
      });
  }

  navigateToPreviousStep(fromStep: ActivationStep): void {
    combineLatest([
      this.navigationMap$,
      this.activationService.selectedItemsRequireOrderForm$,
      this.paramService.navigationSource$,
    ])
      .pipe(take(1))
      .subscribe(([navigationMap, itemsRequireOrderForm, navigationSource]) => {
        const navigationSelection = getPreviousNavigationStep(
          fromStep,
          navigationSource,
          navigationMap,
          itemsRequireOrderForm,
        );
        this.navigateToSelection(navigationSelection);
      });
  }

  clearSelectionsIfFromStepIsConfirm(fromStep: ActivationStep): void {
    if (fromStep === 'confirmation') {
      this.activationService.clearSelections();
    }
  }

  navigateToSelection(navigationSelection: NavigationLink): void {
    if (navigationSelection) {
      if (navigationSelection.windowNavigation) {
        window.location.href = navigationSelection.url;
      } else {
        this.router.navigate([navigationSelection.url], navigationSelection.navOptions);
      }
    }
  }
}

export function getNextNavigationStep(
  fromStep: ActivationStep,
  itemsRequireOrderForm: OrderFormType,
  navigationMap: NavigationLinkMap,
  navigationSource: string,
  order?: Order,
): NavigationLink {
  let navigationSelection: NavigationLink;
  switch (fromStep) {
    case 'selection':
      if (itemsRequireOrderForm === OrderFormType.DYNAMIC) {
        navigationSelection = navigationMap.dynamicOrderForm;
      } else if (itemsRequireOrderForm === OrderFormType.COMMON || itemsRequireOrderForm === OrderFormType.CUSTOM) {
        navigationSelection = navigationMap.orderForm;
      } else {
        navigationSelection = navigationMap.confirmation;
      }
      break;
    case 'dynamic-order-form':
      navigationSelection = navigationMap.orderForm;
      break;
    case 'order-form':
      navigationSelection = navigationMap.confirmation;
      break;
    case 'confirmation':
      if (navigationSource === 'account-create') {
        navigationSelection = navigationMap.manageAccounts;
      } else {
        if (order) {
          navigationSelection = navigationMap.orderSubmitted;
          navigationSelection.navOptions = {
            queryParams: { orderId: order.orderId, accountGroupId: order.businessId },
          };
        } else {
          navigationSelection = navigationMap.accountDetails;
        }
      }
      break;
  }
  return navigationSelection;
}

export function getPreviousNavigationStep(
  fromStep: ActivationStep,
  navigationSource: string,
  navigationMap: NavigationLinkMap,
  itemsRequireOrderForm: OrderFormType,
): NavigationLink {
  let navigationSelection: NavigationLink;
  switch (fromStep) {
    case 'selection':
      if (navigationSource === 'account-create') {
        navigationSelection = navigationMap.manageAccounts;
      } else {
        navigationSelection = navigationMap.accountDetails;
      }
      break;
    case 'dynamic-order-form':
      if (navigationSource === 'account-create') {
        // If we got to the order form from account-create, go back to manage accounts
        navigationSelection = navigationMap.manageAccounts;
      } else {
        navigationSelection = navigationMap.selection;
      }
      break;
    case 'order-form':
      if (navigationSource === 'account-create') {
        // If we got to the order form from account-create, go back to manage accounts
        navigationSelection = navigationMap.manageAccounts;
      } else if (itemsRequireOrderForm === OrderFormType.DYNAMIC) {
        navigationSelection = navigationMap.dynamicOrderForm;
      } else if (navigationSource === 'account-details-edit-resubmit') {
        navigationSelection = navigationMap.accountDetails;
      } else {
        navigationSelection = navigationMap.selection;
      }
      break;
    case 'confirmation':
      if (
        itemsRequireOrderForm === OrderFormType.COMMON ||
        itemsRequireOrderForm === OrderFormType.CUSTOM ||
        itemsRequireOrderForm === OrderFormType.DYNAMIC
      ) {
        navigationSelection = navigationMap.orderForm;
      } else if (navigationSource === 'account-create') {
        // If from account create, go back to manage account
        navigationSelection = navigationMap.manageAccounts;
      } else {
        navigationSelection = navigationMap.selection;
      }
      break;
  }
  return navigationSelection;
}
